// src/pages/admin/Games.jsx
import { useEffect, useMemo, useState } from "react";
import {
  Box, Typography, Card, CardContent, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Grid, Chip, Stack, Tooltip, Tabs, Tab, InputAdornment,
  Link, Snackbar, Alert, RadioGroup, FormControlLabel, Radio, FormLabel, MenuItem, Select
} from "@mui/material";
import {
  InfoOutlined as InfoOutlinedIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  SportsEsports as SportsEsportsIcon,
  ContentCopy as ContentCopyIcon,
  OpenInNew as OpenInNewIcon,
  Publish as PublishIcon,
  Unpublished as UnpublishIcon
} from "@mui/icons-material";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
dayjs.extend(relativeTime);

// ✅ RTDB helpers ONLY (Admin writes to /events; EMP reads from /events)
import {
  watchEvents,
  createGame,
  publishEvent,
  deleteEvent,
  updateEvent,
} from "../../api/events";

/* -------------------- UI atoms -------------------- */
const Pill = ({ icon, label, value, color = "primary" }) => (
  <Stack direction="row" spacing={1} alignItems="center"
    sx={{ px: 1.25, py: 0.75, borderRadius: 999, bgcolor: (t) => t.palette.action.hover }}>
    <Box sx={{
      width: 24, height: 24, borderRadius: "50%",
      bgcolor: (t) => t.palette[color]?.light || t.palette.action.selected,
      color: (t) => t.palette[color]?.dark || t.palette.text.primary,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: 16
    }}>{icon}</Box>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 800 }}>{value}</Typography>
  </Stack>
);

const EmptyState = ({ title, subtitle, icon }) => (
  <Box sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
    <Box sx={{ display: "inline-flex", p: 1.25, borderRadius: 2, bgcolor: "action.hover", mb: 1 }}>
      {icon ?? <InfoOutlinedIcon />}
    </Box>
    <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
    {subtitle && <Typography variant="body2" sx={{ mt: 0.5 }}>{subtitle}</Typography>}
  </Box>
);

/* -------------------- helpers -------------------- */
const fmtRange = (s, e) => {
  const fmt = (t) =>
    new Date(t).toLocaleString("en-IN", {
      year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  return s && e ? `${fmt(s)} → ${fmt(e)}` : "—";
};

const relBadge = (e) => {
  const s = e?.dates?.start ? Number(e.dates.start) : 0;
  const en = e?.dates?.end ? Number(e.dates.end) : 0;
  const now = Date.now();
  if (en && now > en) return `Ended ${dayjs(en).fromNow()}`;
  if (s && now < s) return `Starts ${dayjs(s).fromNow()}`;
  if (s && en && now >= s && now <= en) return `Ends ${dayjs(en).fromNow()}`;
  return "";
};

const minutesBetween = (s, e) => (!s || !e ? null : Math.max(1, Math.round((e - s) / 60000)));
const monthKey = (t) => dayjs(t).format("YYYY-MM");

/* -------------------- Component -------------------- */
export default function Games() {
  const [items, setItems] = useState([]); // RTDB /events of type GAME
  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    startAt: dayjs(),
    endAt: dayjs().add(7, "day"),
    gameUrl: "",
    attemptsPerUser: 3,
    tokenDurationMinutes: 15,
    scoringMode: "score", // "score" | "time"
    createAs: "PUBLISHED", // default create as PUBLISHED; can switch to DRAFT
  });

  const [query, setQuery] = useState("");
  const [tab, setTab] = useState(0); // 0=Draft, 1=Upcoming, 2=Ongoing, 3=Past
  const [toast, setToast] = useState({ open: false, msg: "", sev: "success" });
  const [monthFilter, setMonthFilter] = useState("all");

  // 🔴 Load from RTDB (not Core): watch /events and keep only GAME
  useEffect(() => {
    const unsub = watchEvents((obj) => {
      const list = Object.values(obj || {}).filter((e) => e?.type === "GAME");
      setItems(list);
    });
    return () => unsub && unsub();
  }, []);

  // Counters + grouping
  const now = Date.now();
  const groups = useMemo(() => {
    const draft = [], upcoming = [], ongoing = [], past = [];
    (items || []).forEach((e) => {
      if (monthFilter !== "all") {
        const m = monthKey(e?.dates?.start || e?.createdAt || now);
        if (m !== monthFilter) return;
      }
      const status = (e?.status || "").toUpperCase();
      if (status === "DRAFT") { draft.push(e); return; }

      if (status !== "PUBLISHED") return; // we only show PUBLISHED in time buckets
      const s = e?.dates?.start ? Number(e.dates.start) : 0;
      const en = e?.dates?.end ? Number(e.dates.end) : 0;

      if (en && now > en) past.push(e);
      else if (s && now < s) upcoming.push(e);
      else ongoing.push(e);
    });
    return { draft, upcoming, ongoing, past };
  }, [items, now, monthFilter]);

  const sectionData = [groups.draft, groups.upcoming, groups.ongoing, groups.past][tab] || [];

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setToast({ open: true, msg: "Copied", sev: "success" });
    } catch {
      setToast({ open: true, msg: "Copy failed", sev: "error" });
    }
  };

  // Month filter choices from data
  const monthChoices = useMemo(() => {
    const set = new Set();
    (items || []).forEach((e) => {
      const t = e?.dates?.start || e?.createdAt || Date.now();
      set.add(monthKey(t));
    });
    return ["all", ...Array.from(set).sort().reverse()];
  }, [items]);

  /* ✅ CREATE directly in RTDB */
  const handleCreate = async () => {
    const startMs = form.startAt?.toDate ? form.startAt.toDate().getTime() : Number(form.startAt);
    const endMs = form.endAt?.toDate ? form.endAt.toDate().getTime() : Number(form.endAt);
    const durMin = minutesBetween(startMs, endMs);

    if (!startMs || !endMs || endMs <= startMs) {
      return alert("Please select a valid Start and End time (End must be after Start).");
    }

    const payload = {
      status: form.createAs, // "PUBLISHED" or "DRAFT"
      title: form.title.trim() || "Game",
      description: form.description.trim(),
      dates: {
        start: startMs,
        end: endMs,
        entryAllowedAt: startMs,
        durationMinutes: durMin,
      },
      audience: { all: true },
      game: {
        url: (form.gameUrl || "").trim(),
        attemptLimit: Math.max(1, Number(form.attemptsPerUser) || 1),
        tokenDurationMinutes: Math.max(1, Number(form.tokenDurationMinutes) || 15),
        scoringMode: form.scoringMode || "score", // 👈 new
      },
      type: "GAME",
      version: 1,
    };

    try {
      const id = await createGame(payload); // writes /events/{id}
      if (form.createAs === "PUBLISHED") await publishEvent(id);
      setOpenCreate(false);
      setForm({
        title: "",
        description: "",
        startAt: dayjs(),
        endAt: dayjs().add(1, "day"),
        gameUrl: "",
        attemptsPerUser: 3,
        tokenDurationMinutes: 1440,
        scoringMode: "score",
        createAs: "PUBLISHED",
      });
      setToast({ open: true, msg: "Game saved", sev: "success" });
    } catch (err) {
      console.error(err);
      setToast({ open: true, msg: "Create failed", sev: "error" });
    }
  };

  const handlePreview = (e) => {
    const url = e?.game?.url;
    if (url) window.open(url, "_blank", "noopener,noreferrer");
    else setToast({ open: true, msg: "No Preview URL set for this event", sev: "warning" });
  };

  const handleDelete = async (e) => {
    try {
      await deleteEvent(e.id);
      setToast({ open: true, msg: "Deleted", sev: "success" });
    } catch (err) {
      console.error(err);
      setToast({ open: true, msg: "Delete failed", sev: "error" });
    }
  };

  const handleUnpublish = async (e) => {
    try {
      await updateEvent(e.id, { status: "DRAFT" });
      setToast({ open: true, msg: "Unpublished", sev: "success" });
    } catch (err) {
      console.error(err);
      setToast({ open: true, msg: "Unpublish failed", sev: "error" });
    }
  };

  const handlePublish = async (e) => {
    try {
      await publishEvent(e.id);
      setToast({ open: true, msg: "Published", sev: "success" });
    } catch (err) {
      console.error(err);
      setToast({ open: true, msg: "Publish failed", sev: "error" });
    }
  };

  /* -------------------- UI -------------------- */
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ textAlign: "left" }}>
        {/* Header */}
        <Box sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          mb: 2, gap: 2, flexWrap: "wrap",
        }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Games</Typography>

          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: "wrap", gap: 1.25 }}>
            <Pill icon={<SportsEsportsIcon fontSize="small" />} label="Draft" value={groups.draft.length} color="info" />
            <Pill icon={<SportsEsportsIcon fontSize="small" />} label="Upcoming" value={groups.upcoming.length} color="secondary" />
            <Pill icon={<SportsEsportsIcon fontSize="small" />} label="Ongoing" value={groups.ongoing.length} color="success" />

            <TextField size="small" placeholder="Search by title…"
              value={query} onChange={(e) => setQuery(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
              sx={{ minWidth: 220 }}
            />
            <Select
              size="small"
              value={monthFilter}
              onChange={(e) => setMonthFilter(e.target.value)}
              displayEmpty
              sx={{ minWidth: 160 }}
            >
              {monthChoices.map((m) => (
                <MenuItem key={m} value={m}>
                  {m === "all" ? "Filter by month" : dayjs(m + "-01").format("MMM YYYY")}
                </MenuItem>
              ))}
            </Select>

            <Button variant="contained" onClick={() => setOpenCreate(true)} sx={{ borderRadius: 999 }}>
              Create Game
            </Button>
          </Stack>
        </Box>

        {/* Table */}
        <Card>
          <CardContent>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
              <Tab label={`Draft (${groups.draft.length})`} />
              <Tab label={`Upcoming (${groups.upcoming.length})`} />
              <Tab label={`Ongoing (${groups.ongoing.length})`} />
              <Tab label={`Past (${groups.past.length})`} />
            </Tabs>

            {(sectionData || []).length === 0 ? (
              <EmptyState
                title="No games found."
                subtitle="Create a new game to make it visible instantly in EMP."
                icon={<SportsEsportsIcon />}
              />
            ) : (
              <Box component="table" sx={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", padding: "10px 6px" }}>Game</th>
                    <th style={{ textAlign: "left", padding: "10px 6px" }}>Dates</th>
                    <th style={{ textAlign: "left", padding: "10px 6px" }}>Attempts</th>
                    <th style={{ textAlign: "left", padding: "10px 6px" }}>Token (min)</th>
                    <th style={{ textAlign: "left", padding: "10px 6px" }}>Scoring</th>
                    <th style={{ textAlign: "left", padding: "10px 6px" }}>Preview URL</th>
                    <th style={{ textAlign: "left", padding: "10px 6px" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sectionData
                    .filter((e) => (e.title || "").toLowerCase().includes(query.toLowerCase()))
                    .map((e) => {
                      const status = (e?.status || "").toUpperCase();
                      const mode = e?.game?.scoringMode === "time" ? "Time (lower is better)" : "Score (higher is better)";
                      return (
                        <tr key={e.id}>
                          <td style={{ padding: "10px 6px" }}>
                            <Typography sx={{ fontWeight: 700 }}>{e.title}</Typography>
                            <Typography variant="caption" color="text.secondary">{relBadge(e)}</Typography>
                            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                              <Chip size="small" label={`ID: ${e.id}`} onClick={() => copy(e.id)} />
                              <Tooltip title="Copy Event ID">
                                <ContentCopyIcon fontSize="small" sx={{ cursor: "pointer" }} onClick={() => copy(e.id)} />
                              </Tooltip>
                              <Chip size="small" label={status} />
                            </Stack>
                          </td>
                          <td style={{ padding: "10px 6px" }}>{fmtRange(e?.dates?.start, e?.dates?.end)}</td>
                          <td style={{ padding: "10px 6px" }}>{e?.game?.attemptLimit ?? "—"}</td>
                          <td style={{ padding: "10px 6px" }}>{e?.game?.tokenDurationMinutes ?? "—"}</td>
                          <td style={{ padding: "10px 6px" }}>
                            <Chip size="small" color={e?.game?.scoringMode === "time" ? "warning" : "success"} label={mode} />
                          </td>
                          <td style={{ padding: "10px 6px" }}>
                            {e?.game?.url ? (
                              <Link href={e.game.url} target="_blank" rel="noreferrer" underline="hover">
                                Open <OpenInNewIcon sx={{ fontSize: 14, ml: 0.5 }} />
                              </Link>
                            ) : "—"}
                          </td>
                          <td style={{ padding: "10px 6px", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                            <Button size="small" startIcon={<VisibilityIcon />} onClick={() => handlePreview(e)}>
                              Preview
                            </Button>
                            {status === "PUBLISHED" ? (
                              <Button size="small" startIcon={<UnpublishIcon />} onClick={() => handleUnpublish(e)}>
                                Unpublish
                              </Button>
                            ) : (
                              <Button size="small" startIcon={<PublishIcon />} onClick={() => handlePublish(e)}>
                                Publish
                              </Button>
                            )}
                            <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => handleDelete(e)}>
                              Delete
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* ---------- CREATE GAME (RTDB) ---------- */}
        <Dialog open={openCreate} onClose={() => setOpenCreate(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Create Game</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth label="Title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline minRows={2} label="Description"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </Grid>
              <Grid item xs={12} md={6}>
                <DateTimePicker label="Start"
                  value={form.startAt}
                  onChange={(v) => setForm((f) => ({ ...f, startAt: v }))} slotProps={{ textField: { fullWidth: true } }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <DateTimePicker label="End"
                  value={form.endAt}
                  onChange={(v) => setForm((f) => ({ ...f, endAt: v }))} slotProps={{ textField: { fullWidth: true } }} />
              </Grid>

              <Grid item xs={12}>
                <FormLabel component="legend">Scoring mode</FormLabel>
                <RadioGroup
                  row
                  value={form.scoringMode}
                  onChange={(e) => setForm({ ...form, scoringMode: e.target.value })}
                >
                  <FormControlLabel value="score" control={<Radio />} label="Score (higher is better)" />
                  <FormControlLabel value="time" control={<Radio />} label="Time (lower is better)" />
                </RadioGroup>
              </Grid>

              <Grid item xs={12} md={8}>
                <TextField fullWidth label="Preview URL (optional)"
                  placeholder="https://…"
                  value={form.gameUrl}
                  onChange={(e) => setForm({ ...form, gameUrl: e.target.value })} />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField fullWidth type="number" label="Attempts"
                  value={form.attemptsPerUser}
                  onChange={(e) => setForm({ ...form, attemptsPerUser: Math.max(1, Number(e.target.value || 1)) })} />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField fullWidth type="number" label="Token (min)"
                  value={form.tokenDurationMinutes}
                  onChange={(e) => setForm({ ...form, tokenDurationMinutes: Math.max(1, Number(e.target.value || 1)) })} />
              </Grid>

              <Grid item xs={12}>
                <FormLabel component="legend">Visibility</FormLabel>
                <RadioGroup
                  row
                  value={form.createAs}
                  onChange={(e) => setForm({ ...form, createAs: e.target.value })}
                >
                  <FormControlLabel value="PUBLISHED" control={<Radio />} label="Publish now" />
                  <FormControlLabel value="DRAFT" control={<Radio />} label="Save as draft" />
                </RadioGroup>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenCreate(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleCreate}>
              {form.createAs === "PUBLISHED" ? "Create & Publish" : "Create Draft"}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={toast.open} autoHideDuration={2500} onClose={() => setToast({ ...toast, open: false })}>
          <Alert severity={toast.sev} variant="filled">{toast.msg}</Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
}
